import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Search,
  Plus,
  Minus,
  X,
  Check,
  ShieldCheck,
  Truck,
  Stethoscope,
  Bell,
  Zap,
  Heart,
  Droplet,
  Wind,
  Activity,
  MessageCircle,
  Upload,
  ArrowRight,
  ArrowLeft,
  Loader2,
} from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { contact } from "@/lib/healic-data";
import {
  listCareplusMedicines,
  submitCareplusSubscription,
  type CarePlusMedicine,
} from "@/lib/care-plus.functions";

const SITE_URL = "https://healic.in";

export const Route = createFileRoute("/pharmacy")({
  head: () => ({
    meta: [
      { title: "Healic Care+ — Monthly Medicine Subscription at Fixed Price" },
      {
        name: "description",
        content:
          "Healic Parivaar Care+ delivers your monthly medicines at a fixed price, home delivered across Gurugram, Ghaziabad, Haridwar and Agra. Flat 15% off MRP, free doctor teleconsult, BP & sugar check.",
      },
      { property: "og:title", content: "Healic Care+ — Monthly Medicine Subscription" },
      {
        property: "og:description",
        content: "Fixed-price monthly medicines, home delivered. 15% off MRP, locked for 12 months.",
      },
      { property: "og:url", content: `${SITE_URL}/pharmacy` },
      { property: "og:type", content: "website" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/pharmacy` }],
  }),
  component: PharmacyPage,
});

const DISCOUNT_RATE = 0.15;
const FREE_DELIVERY_THRESHOLD = 80000; // paise (₹800)
const DELIVERY_FEE = 4900; // paise

const CATEGORIES: {
  id: CarePlusMedicine["category"];
  name: string;
  hint: string;
  Icon: typeof Heart;
}[] = [
  { id: "bp", name: "BP / Heart", hint: "Blood pressure, cardiac", Icon: Heart },
  { id: "diabetes", name: "Diabetes", hint: "Sugar", Icon: Droplet },
  { id: "thyroid", name: "Thyroid", hint: "", Icon: Activity },
  { id: "kidney", name: "Kidney", hint: "CKD", Icon: Activity },
  { id: "lung", name: "Asthma / COPD", hint: "Saans", Icon: Wind },
  { id: "other", name: "Other", hint: "Search below", Icon: Plus },
];

const DELIVERY_DAYS = ["1st", "5th", "10th", "15th", "20th", "25th"] as const;

const CITIES = [
  "Sohna Road, Gurugram",
  "Indirapuram, Ghaziabad",
  "Haridwar",
  "Agra",
] as const;

const BENEFITS = [
  {
    Icon: Stethoscope,
    tag: "FREE",
    title: "Doctor teleconsult",
    desc: "Every 3 months, with Rx renewal",
  },
  { Icon: Droplet, tag: "FREE", title: "BP & sugar check", desc: "Walk in to any Healic hospital" },
  { Icon: Bell, tag: "FREE", title: "WhatsApp reminders", desc: "Dawai lene ka roz reminder" },
  { Icon: Zap, tag: "FREE", title: "Priority OPD queue", desc: "Members skip the line" },
  {
    Icon: ShieldCheck,
    tag: "MEMBER OFFER",
    title: "5% off family surgery",
    desc: "On any fixed-price package",
  },
  { Icon: Activity, tag: "FREE", title: "Annual health report", desc: "Your year of vitals, summarised" },
];

function rupees(paise: number) {
  return "₹" + Math.round(paise / 100).toLocaleString("en-IN");
}

type BasketItem = { medicine: CarePlusMedicine; qty: number };

function PharmacyPage() {
  const list = useServerFn(listCareplusMedicines);
  const submit = useServerFn(submitCareplusSubscription);
  const {
    data: catalog = { medicines: [], unavailable: false },
    isFetching: isCatalogFetching,
    refetch: refetchCatalog,
  } = useQuery({
    queryKey: ["care-plus", "medicines"],
    queryFn: () => list(),
  });
  const { medicines, unavailable: isCatalogUnavailable } = catalog;

  const [selCats, setSelCats] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");
  const [basket, setBasket] = useState<Record<string, BasketItem>>({});
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  // form
  const [fName, setFName] = useState("");
  const [fPhone, setFPhone] = useState("");
  const [fCity, setFCity] = useState<(typeof CITIES)[number]>(CITIES[0]);
  const [fPin, setFPin] = useState("");
  const [fAddr, setFAddr] = useState("");
  const [fDay, setFDay] = useState<(typeof DELIVERY_DAYS)[number]>("1st");
  const [formErr, setFormErr] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  const filteredMeds = useMemo(() => {
    const q = query.trim().toLowerCase();
    return medicines.filter((m) => {
      const catOk = selCats.size === 0 || selCats.has(m.category);
      const qOk = !q || m.brand.toLowerCase().includes(q) || m.salt_pack.toLowerCase().includes(q);
      return q ? qOk : selCats.size ? catOk : false;
    });
  }, [medicines, selCats, query]);

  const items = Object.values(basket);
  const mrpTotal = items.reduce((s, it) => s + it.medicine.mrp_paise * it.qty, 0);
  const discount = Math.round(mrpTotal * DISCOUNT_RATE);
  const subtotal = mrpTotal - discount;
  const deliveryFee = subtotal >= FREE_DELIVERY_THRESHOLD ? 0 : DELIVERY_FEE;
  const monthlyTotal = subtotal + deliveryFee;
  const hasItems = items.length > 0;

  function toggleCat(id: string) {
    setSelCats((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function addToBasket(m: CarePlusMedicine) {
    setBasket((prev) => ({ ...prev, [m.slug]: { medicine: m, qty: 1 } }));
  }
  function incQty(slug: string) {
    setBasket((prev) => {
      const cur = prev[slug];
      if (!cur) return prev;
      return { ...prev, [slug]: { ...cur, qty: Math.min(cur.qty + 1, 10) } };
    });
  }
  function decQty(slug: string) {
    setBasket((prev) => {
      const cur = prev[slug];
      if (!cur) return prev;
      if (cur.qty <= 1) {
        const { [slug]: _drop, ...rest } = prev;
        return rest;
      }
      return { ...prev, [slug]: { ...cur, qty: cur.qty - 1 } };
    });
  }
  function removeFromBasket(slug: string) {
    setBasket((prev) => {
      const { [slug]: _drop, ...rest } = prev;
      return rest;
    });
  }

  function goToDelivery() {
    if (!hasItems) return;
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function goToReview() {
    setFormErr(null);
    if (!fName.trim()) return setFormErr("Please enter the patient name.");
    if (!/^[6-9]\d{9}$/.test(fPhone.trim()))
      return setFormErr("Please enter a valid 10-digit mobile number.");
    if (!/^\d{6}$/.test(fPin.trim())) return setFormErr("Please enter a valid 6-digit PIN code.");
    if (fAddr.trim().length < 6) return setFormErr("Please enter your delivery address.");
    setStep(3);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSubmit() {
    setSubmitErr(null);
    setSubmitting(true);
    try {
      await submit({
        data: {
          full_name: fName.trim(),
          phone: fPhone.trim(),
          city: fCity,
          pincode: fPin.trim(),
          address: fAddr.trim(),
          delivery_day: fDay,
          items: items.map((it) => ({
            medicine_slug: it.medicine.slug,
            quantity: it.qty,
          })),
        },
      });
      setStep(4);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setSubmitErr(
        e instanceof Error ? e.message : "Something went wrong. Please try again or WhatsApp us.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-accent/25 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:py-16 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-pill border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
            <span className="h-1.5 w-1.5 rounded-full bg-accent" />
            Parivaar Care+ · Monthly Medicine Subscription
          </span>
          <h1 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight sm:text-4xl lg:text-5xl">
            Aapki monthly dawaiyan.{" "}
            <span className="text-accent">Fixed price. Ghar par. Har mahine.</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm opacity-90 sm:text-base">
            Choose your medicines once — we deliver them before they run out, at a locked price for
            12 months. Tension-free, jaise Healic ki surgery.
          </p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3 text-xs opacity-90 sm:text-sm">
            {[
              ["Flat 15% off MRP", "locked for 1 year"],
              ["Free doctor teleconsult", "every 3 months"],
              ["Free BP & sugar check", "at any Healic hospital"],
              ["Delivery in", "Gurugram · Ghaziabad · Agra · Haridwar"],
            ].map(([b, rest]) => (
              <div key={b} className="flex items-start gap-2">
                <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-accent text-[10px] text-accent-foreground">
                  <Check className="h-2.5 w-2.5" />
                </span>
                <span>
                  <b className="font-semibold">{b}</b> <span className="opacity-80">— {rest}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stepper + wizard */}
      <section className="relative z-10 mx-auto -mt-6 max-w-7xl px-4 pb-16 lg:px-8">
        <div className="mb-6 grid grid-cols-3 overflow-hidden rounded-2xl bg-card shadow-card">
          {[
            [1, "Choose medicines"],
            [2, "Delivery details"],
            [3, "Confirm & pay"],
          ].map(([n, label]) => {
            const num = n as number;
            const active = step === num;
            const done = step > num;
            return (
              <div
                key={num}
                className={`flex items-center justify-center gap-2 border-b-4 px-3 py-3 text-center text-xs font-semibold transition ${
                  active
                    ? "border-accent text-primary"
                    : done
                      ? "border-primary/60 text-primary"
                      : "border-transparent text-muted-foreground"
                }`}
              >
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] ${
                    active
                      ? "bg-accent text-accent-foreground"
                      : done
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {done ? <Check className="h-3 w-3" /> : num}
                </span>
                <span className="hidden sm:inline">{label}</span>
              </div>
            );
          })}
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start">
          <div>
            {step === 1 && (
              <div className="rounded-3xl border border-border bg-card p-6 shadow-card sm:p-7">
                <h2 className="font-display text-xl font-semibold text-primary sm:text-2xl">
                  Which conditions do you take medicines for?
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Select one or more — we'll show the common medicines. Aap search bhi kar sakte
                  hain.
                </p>

                <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-6">
                  {CATEGORIES.map((c) => {
                    const on = selCats.has(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleCat(c.id)}
                        className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 px-2 py-3 text-center transition ${
                          on
                            ? "border-accent bg-accent/10 text-primary"
                            : "border-border bg-background text-foreground hover:border-accent/60"
                        }`}
                      >
                        <c.Icon className={`h-6 w-6 ${on ? "text-accent" : "text-primary/70"}`} />
                        <span className="text-sm font-semibold leading-tight">{c.name}</span>
                        {c.hint && <span className="text-[11px] text-muted-foreground">{c.hint}</span>}
                      </button>
                    );
                  })}
                </div>

                <div className="relative mt-5">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search medicine — e.g. Telma, Foracort, Metformin…"
                    aria-label="Search medicines"
                    className="w-full rounded-pill border border-border bg-background px-4 py-3 pl-11 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/30"
                  />
                </div>

                <div className="mt-4 flex max-h-[460px] flex-col gap-2 overflow-y-auto pr-1">
                  {isCatalogUnavailable ? (
                    <div className="rounded-xl border border-border bg-muted/30 px-4 py-8 text-center">
                      <p className="text-sm font-medium text-foreground">
                        The medicine list is temporarily unavailable.
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Please try again. You can also send your prescription on WhatsApp.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="mt-4"
                        disabled={isCatalogFetching}
                        onClick={() => void refetchCatalog()}
                      >
                        {isCatalogFetching ? <Loader2 className="animate-spin" /> : null}
                        Try again
                      </Button>
                    </div>
                  ) : filteredMeds.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-8 text-center text-sm text-muted-foreground">
                      {query
                        ? "No match — humein WhatsApp par bataiye, hum arrange kar denge."
                        : "Select a condition above or search — aapki dawaiyan yahan dikhengi."}
                    </div>
                  ) : (
                    filteredMeds.map((m) => {
                      const inB = basket[m.slug];
                      const cp = Math.round(m.mrp_paise * (1 - DISCOUNT_RATE));
                      return (
                        <div
                          key={m.slug}
                          className={`flex items-center gap-3 rounded-2xl border-2 p-3 sm:p-4 ${
                            inB ? "border-primary/60 bg-primary-soft/40" : "border-border bg-background"
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-semibold text-foreground">
                                {m.brand}
                              </span>
                              {m.requires_rx && (
                                <span className="rounded border border-accent px-1 text-[9px] font-bold uppercase tracking-wide text-accent">
                                  Rx
                                </span>
                              )}
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground">{m.salt_pack}</div>
                          </div>
                          <div className="text-right leading-tight">
                            <div className="text-[11px] text-muted-foreground line-through">
                              {rupees(m.mrp_paise)}
                            </div>
                            <div className="text-sm font-bold text-primary">{rupees(cp)}</div>
                            <div className="text-[10px] text-muted-foreground">per pack</div>
                          </div>
                          {inB ? (
                            <div className="flex items-center overflow-hidden rounded-lg border border-border">
                              <button
                                type="button"
                                aria-label="Decrease"
                                onClick={() => decQty(m.slug)}
                                className="flex h-8 w-8 items-center justify-center text-primary hover:bg-accent/10"
                              >
                                <Minus className="h-3.5 w-3.5" />
                              </button>
                              <span className="w-7 text-center text-sm font-semibold">{inB.qty}</span>
                              <button
                                type="button"
                                aria-label="Increase"
                                onClick={() => incQty(m.slug)}
                                className="flex h-8 w-8 items-center justify-center text-primary hover:bg-accent/10"
                              >
                                <Plus className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => addToBasket(m)}
                              className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
                            >
                              + Add
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="mt-5 flex items-start gap-3 rounded-2xl border border-dashed border-accent bg-accent/10 p-4">
                  <Upload className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                  <div className="flex-1 text-sm text-foreground">
                    <b className="font-semibold text-primary">Prescription required for Rx medicines.</b>{" "}
                    Upload later on WhatsApp. Our pharmacist verifies every order before dispatch.
                  </div>
                  <a
                    href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Hi Healic, I want to send my Care+ prescription.")}`}
                    className="whitespace-nowrap rounded-lg border border-primary bg-background px-3 py-2 text-xs font-semibold text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    Send on WhatsApp
                  </a>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    disabled={!hasItems}
                    onClick={goToDelivery}
                    className="inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Continue to delivery <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="rounded-3xl border border-border bg-card p-6 shadow-card sm:p-7">
                <h2 className="font-display text-xl font-semibold text-primary sm:text-2xl">
                  Where should we deliver?
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Delivery is free on orders above ₹800/month. Aapke nazdeeki Healic hospital ki
                  pharmacy se.
                </p>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <Field label="Full name">
                    <input
                      value={fName}
                      onChange={(e) => setFName(e.target.value)}
                      placeholder="Patient / family member"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Mobile number">
                    <input
                      inputMode="tel"
                      maxLength={10}
                      value={fPhone}
                      onChange={(e) => setFPhone(e.target.value.replace(/\D/g, ""))}
                      placeholder="10-digit mobile"
                      className={inputCls}
                    />
                  </Field>
                  <Field label="City">
                    <select
                      value={fCity}
                      onChange={(e) => setFCity(e.target.value as (typeof CITIES)[number])}
                      className={inputCls}
                    >
                      {CITIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="PIN code">
                    <input
                      inputMode="numeric"
                      maxLength={6}
                      value={fPin}
                      onChange={(e) => setFPin(e.target.value.replace(/\D/g, ""))}
                      placeholder="e.g. 122018"
                      className={inputCls}
                    />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Delivery address">
                      <input
                        value={fAddr}
                        onChange={(e) => setFAddr(e.target.value)}
                        placeholder="House no., street, landmark"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Refill delivery date — same date every month">
                      <div className="flex flex-wrap gap-2">
                        {DELIVERY_DAYS.map((d) => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => setFDay(d)}
                            className={`rounded-xl border-2 px-4 py-2 text-sm font-semibold transition ${
                              d === fDay
                                ? "border-accent bg-accent/10 text-primary"
                                : "border-border bg-background text-foreground hover:border-accent/60"
                            }`}
                          >
                            {d} of month
                          </button>
                        ))}
                      </div>
                    </Field>
                  </div>
                </div>

                {formErr && (
                  <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {formErr}
                  </p>
                )}

                <div className="mt-6 flex justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="inline-flex items-center gap-2 rounded-pill border border-border bg-background px-5 py-3 text-sm font-semibold text-primary"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <button
                    type="button"
                    onClick={goToReview}
                    className="inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                  >
                    Review subscription <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="rounded-3xl border border-border bg-card p-6 shadow-card sm:p-7">
                <h2 className="font-display text-xl font-semibold text-primary sm:text-2xl">
                  Confirm your Parivaar Care+ plan
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Our care coordinator will call within 2 hours to verify your Rx and set up monthly
                  UPI AutoPay. Cancel anytime — koi lock-in nahi.
                </p>

                <div className="mt-5 rounded-2xl border border-border p-5">
                  <div className="mb-3 text-sm font-semibold text-primary">Monthly basket</div>
                  {items.map((it) => (
                    <div
                      key={it.medicine.slug}
                      className="flex justify-between border-b border-border/60 py-2 text-sm last:border-0"
                    >
                      <span className="text-foreground">
                        {it.medicine.brand} <span className="text-muted-foreground">×{it.qty}</span>
                      </span>
                      <span className="font-semibold text-primary">
                        {rupees(Math.round(it.medicine.mrp_paise * (1 - DISCOUNT_RATE)) * it.qty)}
                      </span>
                    </div>
                  ))}
                  <div className="mt-3 flex justify-between border-t border-border pt-3 text-sm">
                    <span className="font-bold text-foreground">Total every month</span>
                    <span className="font-bold text-primary">{rupees(monthlyTotal)}</span>
                  </div>
                </div>

                <div className="mt-4 rounded-2xl border border-border p-5 text-sm">
                  <div className="mb-1 font-semibold text-primary">Delivery</div>
                  <div className="text-foreground">
                    {fName} · {fCity}
                  </div>
                  <div className="text-muted-foreground">
                    Every month on the <b className="text-foreground">{fDay}</b> — dispatched 5 days
                    early so you never run out.
                  </div>
                </div>

                {submitErr && (
                  <p className="mt-4 rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {submitErr}
                  </p>
                )}

                <div className="mt-6 flex justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="inline-flex items-center gap-2 rounded-pill border border-border bg-background px-5 py-3 text-sm font-semibold text-primary"
                  >
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="inline-flex items-center gap-2 rounded-pill bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground disabled:opacity-70"
                  >
                    {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                    Start subscription
                  </button>
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="rounded-3xl border border-border bg-card p-10 text-center shadow-card">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-soft text-primary">
                  <Check className="h-8 w-8" />
                </div>
                <h2 className="mt-5 font-display text-2xl font-bold text-primary">
                  Welcome to Parivaar Care+!
                </h2>
                <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
                  Your care coordinator will call within 2 hours to verify your prescription and
                  confirm your first delivery. Aapki dawai ab kabhi khatam nahi hogi.
                </p>
                <a
                  href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Hi Healic, I just signed up for Care+ subscription.")}`}
                  className="mt-6 inline-flex items-center gap-2 rounded-pill bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground"
                >
                  <MessageCircle className="h-4 w-4" /> Confirm on WhatsApp
                </a>
              </div>
            )}

            {/* Benefits */}
            <div className="mt-10">
              <h3 className="font-display text-xl font-semibold text-primary">
                Included free with every subscription
              </h3>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {BENEFITS.map((b) => (
                  <div
                    key={b.title}
                    className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4"
                  >
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                      <b.Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-primary">
                        {b.tag}
                      </div>
                      <div className="text-sm font-semibold text-foreground">{b.title}</div>
                      <div className="text-xs text-muted-foreground">{b.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Summary card */}
          <aside className="lg:sticky lg:top-24">
            <div className="overflow-hidden rounded-2xl bg-primary text-primary-foreground shadow-card">
              <div className="border-b border-white/10 px-5 py-4">
                <div className="text-[11px] font-semibold uppercase tracking-widest opacity-70">
                  Aapka fixed monthly price
                </div>
                <div className="mt-0.5 font-display text-lg font-bold">Care+ Monthly Basket</div>
              </div>
              <div className="px-5 py-4">
                {!hasItems ? (
                  <p className="py-2 text-sm opacity-75">
                    Apni dawaiyan choose kijiye — aapka fixed price yahan dikhega. No hidden charges,
                    kabhi nahi.
                  </p>
                ) : (
                  <>
                    <div className="mb-3 flex max-h-40 flex-col gap-2 overflow-y-auto text-sm">
                      {items.map((it) => (
                        <div key={it.medicine.slug} className="flex justify-between gap-2">
                          <span className="min-w-0 truncate opacity-90">
                            {it.medicine.brand}{" "}
                            <span className="opacity-60">×{it.qty}</span>
                          </span>
                          <span className="flex items-center gap-1.5 whitespace-nowrap font-semibold">
                            {rupees(
                              Math.round(it.medicine.mrp_paise * (1 - DISCOUNT_RATE)) * it.qty,
                            )}
                            <button
                              type="button"
                              aria-label={`Remove ${it.medicine.brand}`}
                              onClick={() => removeFromBasket(it.medicine.slug)}
                              className="text-accent hover:opacity-80"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="space-y-1.5 text-xs opacity-90">
                      <Row label="Monthly MRP total" value={rupees(mrpTotal)} />
                      <Row
                        label="Care+ discount (15%)"
                        value={<span className="text-accent">−{rupees(discount)}</span>}
                      />
                      <Row
                        label="Delivery"
                        value={deliveryFee === 0 ? "FREE" : rupees(deliveryFee)}
                      />
                    </div>
                    <div className="mt-3 border-t border-white/10 pt-3">
                      <div className="text-[11px] uppercase tracking-wider opacity-70">
                        You pay every month
                      </div>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="font-display text-3xl font-bold">
                          {rupees(monthlyTotal)}
                        </span>
                        <span className="text-sm line-through opacity-60">{rupees(mrpTotal)}</span>
                      </div>
                      <span className="mt-2 inline-block rounded-pill bg-accent px-3 py-1 text-xs font-bold text-accent-foreground">
                        You save {rupees(discount * 12)} every year
                      </span>
                      <p className="mt-3 flex items-start gap-2 text-[11px] opacity-75">
                        <Check className="mt-0.5 h-3 w-3 flex-shrink-0 text-accent" />
                        Price locked for 12 months — surgery ki tarah, medicine mein bhi no billing
                        surprises.
                      </p>
                    </div>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={
                  step === 1
                    ? goToDelivery
                    : step === 2
                      ? goToReview
                      : step === 3
                        ? handleSubmit
                        : undefined
                }
                disabled={!hasItems || step === 4 || submitting}
                className="block w-full bg-accent px-4 py-4 text-center font-display text-base font-bold text-accent-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:bg-primary-soft disabled:text-muted-foreground"
              >
                {step === 4
                  ? "Subscription started"
                  : !hasItems
                    ? "Add medicines to continue"
                    : step === 1
                      ? "Continue — set up delivery"
                      : step === 2
                        ? "Review subscription"
                        : submitting
                          ? "Submitting…"
                          : "Start subscription"}
              </button>
              <a
                href={`https://wa.me/${contact.whatsapp}?text=${encodeURIComponent("Hi Healic, I want help setting up my Care+ medicine subscription.")}`}
                className="flex items-center justify-center gap-2 border-t border-white/10 bg-white/5 py-3 text-sm font-semibold opacity-80 hover:opacity-100"
              >
                <Truck className="h-4 w-4" /> Need help? Chat on WhatsApp
              </a>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}

const inputCls =
  "w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground outline-none focus:border-accent focus:ring-2 focus:ring-accent/30";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold text-primary">{label}</span>
      {children}
    </label>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className="font-semibold text-primary-foreground">{value}</span>
    </div>
  );
}
